<template>
  <div class="page-container">
    <a-page-header
      title="调用详情"
      @back="$router.back()"
      style="padding: 0 0 16px"
    />

    <a-spin :spinning="loading">
      <template v-if="detail">
        <!-- 基础信息 -->
        <a-card title="基础信息" style="margin-bottom: 16px">
          <a-descriptions :column="2" bordered size="small">
            <a-descriptions-item label="ID">{{ detail.id }}</a-descriptions-item>
            <a-descriptions-item label="状态">
              <a-tag :color="detail.status === 1 ? 'green' : 'red'">
                {{ detail.status === 1 ? '成功' : '失败' }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="模型">{{ detail.model_name || '-' }}</a-descriptions-item>
            <a-descriptions-item label="耗时">{{ detail.duration_ms != null ? detail.duration_ms + ' ms' : '-' }}</a-descriptions-item>
            <a-descriptions-item label="Prompt 长度">{{ detail.prompt_length != null ? detail.prompt_length + ' 字符' : '-' }}</a-descriptions-item>
            <a-descriptions-item label="调用 IP">{{ detail.client_ip || '-' }}</a-descriptions-item>
            <a-descriptions-item label="调用时间" :span="2">{{ formatBeijingDateTime(detail.create_time) }}</a-descriptions-item>
            <a-descriptions-item v-if="detail.error_message" label="错误信息" :span="2">
              <span style="color: red">{{ detail.error_message }}</span>
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 请求参数 -->
        <a-card title="请求参数" style="margin-bottom: 16px">
          <a-descriptions :column="2" bordered size="small">
            <a-descriptions-item label="业务标识 (system_id)" :span="2">{{ detail.system_id || '-' }}</a-descriptions-item>
            <a-descriptions-item label="车牌号 (license_plate)" :span="2">{{ detail.license_plate || '-' }}</a-descriptions-item>
            <a-descriptions-item label="用户ID (user_id)">{{ detail.user_id ?? '-' }}</a-descriptions-item>
            <a-descriptions-item label="设备MAC (device_mac)">{{ detail.device_mac || '-' }}</a-descriptions-item>
            <a-descriptions-item label="软件版本 (software_version)">{{ detail.software_version || '-' }}</a-descriptions-item>
            <a-descriptions-item label="连接设备条形码 (connected_device_barcode)">{{ detail.connected_device_barcode || '-' }}</a-descriptions-item>
            <a-descriptions-item label="连接设备MAC (connected_device_mac)">{{ detail.connected_device_mac || '-' }}</a-descriptions-item>
            <a-descriptions-item label="报告ID (report_id)">{{ detail.report_id || '-' }}</a-descriptions-item>
            <a-descriptions-item label="排放阶段">{{ detail.emission_level || '-' }}</a-descriptions-item>
            <a-descriptions-item label="污染物控制装置">{{ detail.pollutant_type || '-' }}</a-descriptions-item>
            <a-descriptions-item label="扩展标签 (labelJson)" :span="2">
              <pre style="margin: 0; white-space: pre-wrap; font-size: 12px">{{ formatJson(detail.label_json) }}</pre>
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <a-card v-if="detail.is_test !== 1" title="提示词版本测试" style="margin-bottom: 16px">
          <a-space wrap>
            <span>未发布版本：</span>
            <a-select
              v-model:value="selectedVersion"
              :options="unpublishedVersions"
              :loading="versionsLoading"
              :disabled="unpublishedVersions.length === 0 || testing"
              placeholder="请选择版本"
              style="width: 220px"
            />
            <a-button
              type="primary"
              :loading="testing"
              :disabled="!selectedVersion || !detail.prompt_template_id"
              @click="runTest"
            >
              测试
            </a-button>
          </a-space>
          <a-alert
            v-if="!versionsLoading && unpublishedVersions.length === 0"
            type="info"
            show-icon
            message="当前提示词没有可测试的未发布版本"
            style="margin-top: 12px"
          />
          <a-table
            :columns="testColumns"
            :data-source="testList"
            :loading="testsLoading"
            :pagination="testPagination"
            row-key="id"
            size="small"
            style="margin-top: 16px"
            @change="handleTestPageChange"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'status'">
                <a-tag :color="record.status === 1 ? 'green' : 'red'">
                  {{ record.status === 1 ? '成功' : '失败' }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'result_text'">
                <a-tooltip :title="record.result_text || record.error_message || '-'">
                  {{ record.result_text || record.error_message || '-' }}
                </a-tooltip>
              </template>
            </template>
          </a-table>
        </a-card>

        <!-- 完整 Prompt -->
        <a-card title="完整 Prompt" style="margin-bottom: 16px">
          <pre style="max-height: 500px; overflow: auto; white-space: pre-wrap;
                      font-family: monospace; font-size: 12px; background: #f5f5f5;
                      padding: 12px; border-radius: 4px; margin: 0">{{ detail.prompt_content || '（无）' }}</pre>
        </a-card>

        <!-- 结果 -->
        <a-card title="结果 (result)" style="margin-bottom: 16px">
          <pre style="max-height: 300px; overflow: auto; white-space: pre-wrap;
                      font-family: monospace; font-size: 12px; background: #f5f5f5;
                      padding: 12px; border-radius: 4px; margin: 0">{{ detail.result_text || '（无）' }}</pre>
        </a-card>

        <!-- AI 响应 -->
        <a-card title="AI 原始响应">
          <pre style="max-height: 500px; overflow: auto; white-space: pre-wrap;
                      font-family: monospace; font-size: 12px; background: #f5f5f5;
                      padding: 12px; border-radius: 4px; margin: 0">{{ formatJson(detail.response_raw) }}</pre>
        </a-card>
      </template>
    </a-spin>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import { getKnowledgeVersionHistory } from '@/api/knowledge';
import { formatBeijingDateTime } from '@/utils/dateTime';
import {
  getPosttreatmentCheckLogById,
  runPosttreatmentCheckTest,
  getPosttreatmentCheckTests
} from '@/api/posttreatmentCheckLog';

const route = useRoute();
const loading = ref(false);
const detail = ref(null);
const versionsLoading = ref(false);
const versions = ref([]);
const selectedVersion = ref(undefined);
const testing = ref(false);
const testsLoading = ref(false);
const testList = ref([]);
const testPagination = ref({ current: 1, pageSize: 10, total: 0, showSizeChanger: true });

const unpublishedVersions = computed(() => versions.value
  .filter(item => !item.is_published)
  .map(item => ({ label: `v${item.version}${item.is_latest ? '（最新）' : ''}`, value: item.version })));

const testColumns = [
  { title: '测试 ID', dataIndex: 'id', key: 'id', width: 90 },
  { title: '提示词版本', dataIndex: 'prompt_version', key: 'prompt_version', width: 110, customRender: ({ text }) => text ? `v${text}` : '-' },
  { title: '模型', dataIndex: 'model_name', key: 'model_name', width: 160 },
  { title: '结果', dataIndex: 'result_text', key: 'result_text', ellipsis: true },
  { title: '状态', dataIndex: 'status', key: 'status', width: 80 },
  { title: '耗时', dataIndex: 'duration_ms', key: 'duration_ms', width: 100, customRender: ({ text }) => text != null ? `${text} ms` : '-' },
  { title: '测试时间', dataIndex: 'create_time', key: 'create_time', width: 170, customRender: ({ text }) => formatBeijingDateTime(text) }
];

function formatJson(str) {
  if (!str) return '（无）';
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
}

async function fetchDetail() {
  loading.value = true;
  try {
    const res = await getPosttreatmentCheckLogById(route.params.id);
    if (res.data) {
      detail.value = res.data;
      await Promise.all([fetchVersions(), fetchTests()]);
    }
  } finally {
    loading.value = false;
  }
}

async function fetchVersions() {
  if (!detail.value?.prompt_template_id) return;
  versionsLoading.value = true;
  try {
    const res = await getKnowledgeVersionHistory(detail.value.prompt_template_id);
    versions.value = res.data || [];
    selectedVersion.value = unpublishedVersions.value[0]?.value;
  } finally {
    versionsLoading.value = false;
  }
}

async function fetchTests() {
  if (!route.params.id) return;
  testsLoading.value = true;
  try {
    const res = await getPosttreatmentCheckTests(route.params.id, {
      page: testPagination.value.current,
      pageSize: testPagination.value.pageSize
    });
    testList.value = res.data?.items || [];
    testPagination.value.total = res.data?.total || 0;
  } finally {
    testsLoading.value = false;
  }
}

async function runTest() {
  testing.value = true;
  try {
    await runPosttreatmentCheckTest({
      source_log_id: Number(route.params.id),
      prompt_template_id: detail.value.prompt_template_id,
      prompt_version: selectedVersion.value
    });
    message.success('测试调用完成');
    testPagination.value.current = 1;
    await fetchTests();
  } finally {
    testing.value = false;
  }
}

function handleTestPageChange(pagination) {
  testPagination.value.current = pagination.current;
  testPagination.value.pageSize = pagination.pageSize;
  fetchTests();
}

onMounted(fetchDetail);
</script>
